import React, {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  useEditor,
  EditorContent as TipTapEditorContent,
  JSONContent,
} from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import { ResizableImage } from "./extensions/ResizableImageExtension";
import { TableKitPlus } from "tiptap-table-plus";
import {
  StyledTableCell,
  StyledTableHeader,
  StyledTableRow,
} from "./extensions/TableCellStyleExtension";
import { PaginationPlus } from "tiptap-pagination-plus";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import {
  TextStyle,
  FontFamily,
  FontSize,
  Color,
  BackgroundColor,
} from "@tiptap/extension-text-style";
import { Mathematics } from "@tiptap/extension-mathematics";
// Link and Underline are bundled in StarterKit v3 — configured there to avoid duplicates.
import TableContextMenu from "./TableContextMenu";
import { CommentExtension } from "./extensions/CommentExtension";
import {
  TrackChangesExtension,
  InsertionMark,
  DeletionMark,
  getTrackChanges,
  TrackChangeInfo,
} from "./extensions/TrackChangesExtension";
import {
  UniqueIdExtension,
  findNodeById,
} from "./extensions/UniqueIdExtension";
import { ParagraphStyleExtension } from "./extensions/ParagraphStyleExtension";
import { PageBreak } from "./extensions/PageBreakExtension";
import { TableOfContentsExtension } from "./extensions/TableOfContentsExtension";
import { ColumnsExtension } from "./extensions/ColumnsExtension";
import {
  FootnoteExtensions,
  getFootnotes,
  FootnoteKind,
  FootnoteInfo,
} from "./extensions/FootnoteExtension";
import HeaderFooterInlineEditor from "./HeaderFooterInlineEditor";
import type { ParsedHeaderFooterConfig } from "./utils/ooxmlParser/types";
import {
  composeSectionHtml,
  normalizeParsedConfig,
  renderPageTokens,
  sanitizeZoneHtml,
  HEADER_PAGE_NUM_CLASS,
  FOOTER_PAGE_NUM_CLASS,
} from "./utils/headerFooterZones";
import {
  PageSetup,
  normalizePageSetup,
  pageSetupToPagination,
} from "./utils/pageSetup";
import {
  serializeForLLM,
  executeOperations,
  validatePatch,
  SerializedNode,
  OperationPatch,
  PatchExecutionResult,
} from "./operations";

import EditorToolbar from "./EditorToolbar";
import EditorMenuBar from "./EditorMenuBar";
import MathInputModal from "./MathInputModal";
import SpecialCharacterModal from "./SpecialCharacterModal";
import WatermarkModal from "./WatermarkModal";
import FindReplaceBar from "./FindReplaceBar";
import LinkModal from "./LinkModal";
import LinkBubble from "./LinkBubble";
import KeyboardShortcutsModal from "./KeyboardShortcutsModal";
import {
  flattenNestedTables,
  hasDeeplyNestedTables,
} from "./utils/flattenNestedTables";
import { canonicalizeFontFamily } from "./utils/fontMap";
import FloatingAnnotationsLayer from "./FloatingAnnotationsLayer";
import "./TipTapEditor.css";
import "katex/dist/katex.min.css";

export interface TipTapEditorContentData {
  html: string;
  json: JSONContent;
  text: string;
}

export interface CommentData {
  id: string;
  content: string;
  author: string;
  createdAt: Date;
  replies?: CommentData[];
  resolvedAt?: Date;
}

// Header/Footer configuration types
export interface HeaderFooterConfig {
  headerLeft?: string;
  headerRight?: string;
  footerLeft?: string;
  footerRight?: string;
}

export interface PerPageHeaderFooter {
  pageNumber: number;
  headerLeft?: string;
  headerRight?: string;
  footerLeft?: string;
  footerRight?: string;
}

export interface HeaderFooterClickEvent {
  event: MouseEvent;
  pageNumber: number;
}

export interface TipTapEditorProps {
  initialContent?: string | JSONContent;
  onChange?: (content: TipTapEditorContentData) => void;
  onCommentActivated?: (commentId: string | null) => void;
  onTrackChangeActivated?: (
    changeId: string | null,
    type: "insertion" | "deletion" | null
  ) => void;
  onTrackingStateChanged?: (enabled: boolean) => void;
  onNewDocument?: () => void;
  onExportDocx?: () => void;
  onImportDocx?: (file: File) => void;
  onImportPdf?: (file: File) => void;
  readOnly?: boolean;
  placeholder?: string;
  showToolbar?: boolean;
  className?: string;
  // Save
  onSave?: () => void;
  isSaving?: boolean;
  showUnsavedBadge?: boolean;
  // Version history
  onVersionHistory?: () => void;
  // Track changes (toolbar)
  trackChangesEnabled?: boolean;
  onToggleTrackChanges?: () => void;
  trackChanges?: TrackChangeInfo[];
  currentChangeIndex?: number;
  onNavigateChange?: (direction: "prev" | "next") => void;
  onAcceptChange?: (changeId: string) => void;
  onRejectChange?: (changeId: string) => void;
  onAcceptAllChanges?: () => void;
  onRejectAllChanges?: () => void;
  // Comments panel (toolbar)
  showCommentsPanel?: boolean;
  onToggleCommentsPanel?: () => void;
  commentsCount?: number;
  // Track changes panel (toolbar)
  showTrackChangesPanel?: boolean;
  onToggleTrackChangesPanel?: () => void;
  trackChangesCount?: number;
  // Text statistics
  onShowTextStats?: () => void;
  // Insert menu
  onAddComment?: () => void;
  // Export PDF / Print
  onExportPdf?: () => void;
  onPrint?: () => void;
  // View - Outline
  showOutlinePanel?: boolean;
  onToggleOutlinePanel?: () => void;
  // Header/Footer configuration
  headerLeft?: string;
  headerRight?: string;
  footerLeft?: string;
  footerRight?: string;
  perPageHeaders?: PerPageHeaderFooter[];
  perPageFooters?: PerPageHeaderFooter[];
  onHeaderClick?: (params: HeaderFooterClickEvent) => void;
  onFooterClick?: (params: HeaderFooterClickEvent) => void;
  onEditHeaderFooter?: () => void;
  // Rich header/footer mode: when provided (even null), clicking a page
  // header/footer opens the in-place 3-zone editor and the legacy
  // headerLeft/... props above are ignored. The editor owns live preview;
  // the parent persists via onHeaderFooterRichChange (fired on close).
  headerFooterRich?: ParsedHeaderFooterConfig | null;
  onHeaderFooterRichChange?: (config: ParsedHeaderFooterConfig) => void;
  // Page size / orientation / margins. Read ONCE at mount — parents apply
  // changes by remounting the editor (key it on pageSetupKey(setup)).
  pageSetup?: PageSetup | null;
  // File menu → "Page setup…" (parent owns the dialog)
  onPageSetup?: () => void;
  // Document watermark text (e.g. "DRAFT", "CONFIDENTIAL"). null/"" = none.
  watermark?: string | null;
  // Persist a watermark change ("" clears it). Parent stores it in meta.watermark.
  onWatermarkChange?: (text: string) => void;
  // Image upload: parent provides a function that uploads a File to GCS and
  // returns the proxy src + GCS path.  When set, images inserted via toolbar,
  // menu bar, or paste are uploaded in the background after an instant base64
  // preview (optimistic rendering).
  onImageUpload?: (file: File) => Promise<{ src: string; gcsFileName: string }>;
  // Current user name — attributed on track change marks and used for future collaboration
  currentUser?: string;

  // ── Floating annotations (Google Docs-style cards in margin) ──
  floatingComments?: CommentData[];
  floatingActiveCommentId?: string | null;
  floatingShowComments?: boolean;
  floatingAiEditingCommentId?: string | null;
  floatingOnCommentClick?: (id: string) => void;
  floatingOnDeleteComment?: (id: string) => void;
  floatingOnResolveComment?: (id: string) => void;
  floatingOnEditComment?: (id: string, newContent: string) => void;
  floatingOnReplyToComment?: (
    id: string,
    content: string,
    author: string
  ) => void;
  floatingOnAIEditFromComment?: (id: string) => void;
  floatingTrackChanges?: import("./TrackChangesPanel").TrackChangeData[];
  floatingActiveChangeId?: string | null;
  floatingShowTrackChanges?: boolean;
  floatingOnChangeClick?: (id: string) => void;
  floatingOnAcceptChange?: (id: string) => void;
  floatingOnRejectChange?: (id: string) => void;
  floatingPendingCommentId?: string | null;
  floatingOnSubmitPendingComment?: (content: string) => void;
  floatingOnCancelPendingComment?: () => void;
  floatingOnDismiss?: () => void;
}

export interface TipTapEditorRef {
  getHTML: () => string;
  getJSON: () => JSONContent;
  getText: () => string;
  setContent: (content: string | JSONContent) => void;
  focus: () => void;
  isEmpty: () => boolean;
  getEditor: () => ReturnType<typeof useEditor>;
  insertContent: (content: string | JSONContent) => void;
  insertImage: (src: string, alt?: string) => void;
  insertTable: (rows?: number, cols?: number) => void;
  addComment: (commentId: string) => void;
  removeComment: (commentId: string) => void;
  scrollToComment: (commentId: string) => boolean;
  scrollToTrackChange: (changeId: string) => boolean;
  // Track changes methods
  getTrackChanges: () => TrackChangeInfo[];
  acceptChange: (changeId: string) => void;
  rejectChange: (changeId: string) => void;
  acceptAllChanges: () => void;
  rejectAllChanges: () => void;
  enableTrackChanges: () => void;
  disableTrackChanges: () => void;
  toggleTrackChanges: () => void;
  isTrackChangesEnabled: () => boolean;
  setTrackChangesAuthor: (author: string) => void;
  getTrackChangesAuthor: () => string | undefined;
  // Apply track change mark to text (for import)
  applyTrackChangeMark: (
    text: string,
    type: "insertion" | "deletion",
    changeId: string,
    author?: string,
    date?: string,
    position?: number,
    contextBefore?: string,
    contextAfter?: string
  ) => boolean;
  // Math/LaTeX methods
  insertMath: (latex: string, isBlock?: boolean) => void;
  // Footnotes / endnotes
  insertFootnote: (kind?: FootnoteKind) => void;
  getFootnotes: () => FootnoteInfo[];
  // === Document Operations API (AI Integration) ===
  getDocumentForLLM: () => SerializedNode[];
  applyOperations: (patch: OperationPatch) => PatchExecutionResult;
  validateOperations: (patch: OperationPatch) => {
    valid: boolean;
    errors: string[];
  };
  findNodeById: (nodeId: string) => { node: any; pos: number } | null;
  lockForAI: () => void;
  unlockFromAI: () => void;
  isLockedForAI: () => boolean;
  // === Comment Sync (for undo/redo support) ===
  getCommentIds: () => string[];
  // === Header/Footer API ===
  updateHeaderContent: (
    left: string,
    right: string,
    pageNumber?: number
  ) => void;
  updateFooterContent: (
    left: string,
    right: string,
    pageNumber?: number
  ) => void;
  getHeaderFooterConfig: () => HeaderFooterConfig;
  // === Zoom API ===
  zoomToFit: () => void;
}

const TipTapEditor = forwardRef<TipTapEditorRef, TipTapEditorProps>(
  (
    {
      initialContent,
      onChange,
      onCommentActivated,
      onTrackChangeActivated,
      onTrackingStateChanged,
      onNewDocument,
      onExportDocx,
      onImportDocx,
      onImportPdf,
      readOnly = false,
      placeholder = "Start typing...",
      showToolbar = true,
      className = "",
      // Save
      onSave,
      isSaving,
      showUnsavedBadge,
      // Version history
      onVersionHistory,
      // Track changes (toolbar)
      trackChangesEnabled,
      onToggleTrackChanges,
      trackChanges,
      currentChangeIndex,
      onNavigateChange,
      onAcceptChange,
      onRejectChange,
      onAcceptAllChanges,
      onRejectAllChanges,
      // Comments panel (toolbar)
      showCommentsPanel,
      onToggleCommentsPanel,
      commentsCount,
      // Track changes panel (toolbar)
      showTrackChangesPanel,
      onToggleTrackChangesPanel,
      trackChangesCount,
      // Text statistics
      onShowTextStats,
      // Insert menu
      onAddComment,
      // Export PDF / Print
      onExportPdf,
      onPrint,
      // View - Outline
      showOutlinePanel,
      onToggleOutlinePanel,
      // Header/Footer configuration
      headerLeft = "",
      headerRight = "",
      footerLeft = "",
      footerRight = "{page}",
      perPageHeaders,
      perPageFooters,
      onHeaderClick,
      onFooterClick,
      onEditHeaderFooter,
      headerFooterRich,
      onHeaderFooterRichChange,
      pageSetup,
      onPageSetup,
      watermark,
      onWatermarkChange,
      onImageUpload,
      currentUser,
      // Floating annotations
      floatingComments,
      floatingActiveCommentId,
      floatingShowComments,
      floatingAiEditingCommentId,
      floatingOnCommentClick,
      floatingOnDeleteComment,
      floatingOnResolveComment,
      floatingOnEditComment,
      floatingOnReplyToComment,
      floatingOnAIEditFromComment,
      floatingTrackChanges,
      floatingActiveChangeId,
      floatingShowTrackChanges,
      floatingOnChangeClick,
      floatingOnAcceptChange,
      floatingOnRejectChange,
      floatingPendingCommentId,
      floatingOnSubmitPendingComment,
      floatingOnCancelPendingComment,
      floatingOnDismiss,
    },
    ref
  ) => {
    // Keep a stable ref to the latest onImageUpload so the paste handler
    // (created once in useEditor) always calls the current callback.
    const onImageUploadRef = useRef(onImageUpload);
    onImageUploadRef.current = onImageUpload;

    // AI lock state - prevents editing during AI operations
    const [isAILocked, setIsAILocked] = useState(false);
    // Math modal state (for Insert menu)
    const [showMathModal, setShowMathModal] = useState(false);
    const [showSpecialCharModal, setShowSpecialCharModal] = useState(false);
    const [showWatermarkModal, setShowWatermarkModal] = useState(false);
    // Zoom level state (percentage, default 100%)
    const [zoomLevel, setZoomLevel] = useState(100);

    // ── Rich header/footer mode (in-place 3-zone editing) ──
    // Mode is fixed at mount: the parent either drives the legacy string
    // props or provides headerFooterRich from the first render.
    const richMode = useRef(headerFooterRich !== undefined).current;
    const [richConfig, setRichConfig] = useState<ParsedHeaderFooterConfig>(() =>
      normalizeParsedConfig(headerFooterRich)
    );
    const richConfigJsonRef = useRef(JSON.stringify(richConfig));
    const [hfEditing, setHfEditing] = useState<{
      kind: "header" | "footer";
      pageNumber: number;
      targetEl: HTMLElement;
    } | null>(null);
    // readOnly is read inside pagination click handlers created once at mount
    const readOnlyRef = useRef(readOnly);
    readOnlyRef.current = readOnly;
    // Page dimensions are fixed for this editor instance (parents remount
    // on page-setup change via a key, so mount-time values are always right)
    const pageDims = useRef(
      pageSetupToPagination(normalizePageSetup(pageSetup))
    ).current;
    // Initial pagination header/footer values (the extension only reads
    // these once — runtime changes go through update commands + DOM patch)
    const initialPagination = useRef(
      richMode
        ? {
            headerLeft: composeSectionHtml(
              richConfig.defaultHeader,
              HEADER_PAGE_NUM_CLASS
            ),
            headerRight: "",
            footerLeft: composeSectionHtml(
              richConfig.defaultFooter,
              FOOTER_PAGE_NUM_CLASS
            ),
            footerRight: "",
            perPageHeaders:
              richConfig.hasTitlePage && richConfig.firstPageHeader
                ? [
                    {
                      pageNumber: 1,
                      headerLeft: composeSectionHtml(
                        richConfig.firstPageHeader,
                        HEADER_PAGE_NUM_CLASS
                      ),
                      headerRight: "",
                    },
                  ]
                : undefined,
            perPageFooters:
              richConfig.hasTitlePage && richConfig.firstPageFooter
                ? [
                    {
                      pageNumber: 1,
                      footerLeft: composeSectionHtml(
                        richConfig.firstPageFooter,
                        FOOTER_PAGE_NUM_CLASS
                      ),
                      footerRight: "",
                    },
                  ]
                : undefined,
          }
        : {
            headerLeft,
            headerRight,
            footerLeft,
            footerRight,
            perPageHeaders,
            perPageFooters,
          }
    ).current;
    // Find & Replace bar state
    const [showFindReplace, setShowFindReplace] = useState(false);
    const [findReplaceMode, setFindReplaceMode] = useState<"find" | "replace">(
      "find"
    );
    // Link modal state
    const [showLinkModal, setShowLinkModal] = useState(false);
    // Keyboard shortcuts modal state
    const [showShortcutsModal, setShowShortcutsModal] = useState(false);
    // Table context menu state
    const [tableMenuPosition, setTableMenuPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6],
          },
          // Configure Link/Underline via StarterKit (they're bundled in v3)
          // to avoid duplicate extension registration warnings.
          link: {
            openOnClick: false,
            autolink: true,
            HTMLAttributes: {
              rel: "noopener noreferrer",
            },
          },
          underline: {},
        }),
        Subscript,
        Superscript,
        TextStyle,
        FontFamily,
        FontSize,
        Color,
        BackgroundColor,
        TextAlign.configure({
          types: ["heading", "paragraph"],
          alignments: ["left", "center", "right", "justify"],
        }),
        ParagraphStyleExtension,
        ResizableImage.configure({
          inline: false,
          allowBase64: true,
          HTMLAttributes: {
            class: "tiptap-image",
            loading: "lazy",
          },
        }),
        // Table Plus for pagination support (TipTap v3)
        // Bundles Table, TableRow, TableCell, TableHeader internally
        // Override cell/header/row with styled versions for DOCX import fidelity
        TableKitPlus.configure({
          table: { resizable: true },
          tableCell: false, // disable default, use StyledTableCell
          tableHeader: false, // disable default, use StyledTableHeader
          tableRow: false, // disable default, use StyledTableRow
        }),
        StyledTableCell,
        StyledTableHeader,
        StyledTableRow,
        // Pagination extension — dimensions from page setup (px @ 96 DPI)
        PaginationPlus.configure({
          pageHeight: pageDims.pageHeight,
          pageWidth: pageDims.pageWidth,
          pageGap: 40,
          pageGapBorderSize: 1,
          pageGapBorderColor: "#d0d0d0",
          pageBreakBackground: "#e8e8e8",
          // Header configuration
          headerLeft: initialPagination.headerLeft,
          headerRight: initialPagination.headerRight,
          // Footer configuration
          footerLeft: initialPagination.footerLeft,
          footerRight: initialPagination.footerRight,
          // Per-page header customization. The library reads `customHeader`
          // keyed by page number with { headerLeft, headerRight } — NOT
          // `perPageHeaders` with { left, right }. The old (wrong) key+shape was
          // silently ignored, so the first-page header variant was dropped on
          // every mount (keyed remount on open / page-setup apply).
          ...(initialPagination.perPageHeaders &&
            initialPagination.perPageHeaders.length > 0 && {
              customHeader: initialPagination.perPageHeaders.reduce(
                (acc, item) => {
                  acc[item.pageNumber] = {
                    headerLeft: item.headerLeft || "",
                    headerRight: item.headerRight || "",
                  };
                  return acc;
                },
                {} as Record<number, { headerLeft: string; headerRight: string }>
              ),
            }),
          // Per-page footer customization (customFooter → { footerLeft, footerRight }).
          ...(initialPagination.perPageFooters &&
            initialPagination.perPageFooters.length > 0 && {
              customFooter: initialPagination.perPageFooters.reduce(
                (acc, item) => {
                  acc[item.pageNumber] = {
                    footerLeft: item.footerLeft || "",
                    footerRight: item.footerRight || "",
                  };
                  return acc;
                },
                {} as Record<number, { footerLeft: string; footerRight: string }>
              ),
            }),
          // Interactive header/footer callbacks. In rich mode the clicks
          // open the in-place editor; otherwise they forward to the props.
          ...((richMode || onHeaderClick) && {
            onHeaderClick: (params: {
              event: MouseEvent;
              pageNumber: number;
            }) => {
              if (richMode) {
                if (readOnlyRef.current) return;
                const el =
                  (params.event.currentTarget as HTMLElement) ||
                  (params.event.target as HTMLElement)?.closest(
                    ".rm-page-header"
                  );
                if (el) {
                  setHfEditing({
                    kind: "header",
                    pageNumber: params.pageNumber,
                    targetEl: el as HTMLElement,
                  });
                }
                return;
              }
              onHeaderClick?.(params);
            },
          }),
          ...((richMode || onFooterClick) && {
            onFooterClick: (params: {
              event: MouseEvent;
              pageNumber: number;
            }) => {
              if (richMode) {
                if (readOnlyRef.current) return;
                const el =
                  (params.event.currentTarget as HTMLElement) ||
                  (params.event.target as HTMLElement)?.closest(
                    ".rm-page-footer"
                  );
                if (el) {
                  setHfEditing({
                    kind: "footer",
                    pageNumber: params.pageNumber,
                    targetEl: el as HTMLElement,
                  });
                }
                return;
              }
              onFooterClick?.(params);
            },
          }),
          marginTop: pageDims.marginTop,
          marginBottom: pageDims.marginBottom,
          marginLeft: pageDims.marginLeft,
          marginRight: pageDims.marginRight,
          contentMarginTop: 10,
          contentMarginBottom: 10,
        }),
        Placeholder.configure({
          placeholder,
          emptyEditorClass: "tiptap-editor-empty",
        }),
        CommentExtension.configure({
          HTMLAttributes: {
            class: "tiptap-comment",
          },
          onCommentActivated: (commentId: string | null) => {
            if (onCommentActivated) {
              onCommentActivated(commentId);
            }
          },
        }),
        // Track Changes (custom implementation)
        InsertionMark,
        DeletionMark,
        TrackChangesExtension.configure({
          author: currentUser,
          onChangeActivated: (changeId, type) => {
            if (onTrackChangeActivated) {
              onTrackChangeActivated(changeId, type);
            }
          },
          onTrackingStateChanged: enabled => {
            if (onTrackingStateChanged) {
              onTrackingStateChanged(enabled);
            }
          },
        }),
        // Mathematics/LaTeX support
        Mathematics.configure({
          katexOptions: {
            throwOnError: false,
            strict: false,
          },
        }),
        // Page Break
        PageBreak.configure({
          HTMLAttributes: {
            class: "tiptap-page-break",
          },
        }),
        // Footnotes & endnotes (refs + notes section + numbering plugin)
        ...FootnoteExtensions,
        // Auto-updating in-document table of contents
        TableOfContentsExtension,
        // Multi-column (newspaper-style) layout
        ColumnsExtension,
        // Unique ID Extension for AI Document Operations API
        UniqueIdExtension.configure({
          types: [
            "paragraph",
            "heading",
            "bulletList",
            "orderedList",
            "listItem",
            "blockquote",
            "codeBlock",
            "table",
            "tableRow",
            "tableCell",
            "tableHeader",
            "footnotesSection",
            "footnoteItem",
          ],
        }),
      ],
      editorProps: {
        handleDOMEvents: {
          // Real mouse clicks on the footnote-ref atom never produce a
          // CLICK event (ProseMirror's mousedown handling of selectable
          // atoms suppresses it), so jumps also trigger on MOUSEDOWN.
          mousedown: (view, event) => {
            const target = event.target as HTMLElement;
            const refEl = target.closest(
              "sup.footnote-ref[data-footnote-id]"
            ) as HTMLElement | null;
            const backrefEl = target.closest(
              ".footnote-num-backref"
            ) as HTMLElement | null;
            if (!refEl && !backrefEl) return false;
            if (!view.dom.contains(target)) return false;
            const footnoteId = refEl
              ? refEl.getAttribute("data-footnote-id")
              : backrefEl!.getAttribute("data-target-footnote-id");
            if (!footnoteId) return false;
            event.preventDefault();
            setTimeout(() => {
              let targetPos = -1;
              view.state.doc.descendants((node, pos) => {
                if (targetPos !== -1) return false;
                if (
                  refEl &&
                  node.type.name === "footnoteItem" &&
                  node.attrs.footnoteId === footnoteId
                ) {
                  targetPos = pos + 2; // into the note's first paragraph
                  return false;
                }
                if (
                  !refEl &&
                  node.type.name === "footnoteRef" &&
                  node.attrs.footnoteId === footnoteId
                ) {
                  targetPos = pos + node.nodeSize; // right after the ref
                  return false;
                }
                return true;
              });
              if (targetPos === -1) return;
              const tr = view.state.tr
                .setSelection(TextSelection.create(view.state.doc, targetPos))
                .scrollIntoView();
              view.dispatch(tr);
              view.focus();
            }, 0);
            return true;
          },
          click: (view, event) => {
            const target = event.target as HTMLElement;

            // Footnote/endnote ref → jump to its note (fallback for
            // synthetic/keyboard activation; real clicks are handled on
            // mousedown above).
            const refEl = target.closest(
              "sup.footnote-ref[data-footnote-id]"
            ) as HTMLElement | null;
            if (refEl && view.dom.contains(refEl)) {
              const footnoteId = refEl.getAttribute("data-footnote-id");
              if (footnoteId) {
                event.preventDefault();
                setTimeout(() => {
                  let itemPos = -1;
                  view.state.doc.descendants((node, pos) => {
                    if (
                      itemPos === -1 &&
                      node.type.name === "footnoteItem" &&
                      node.attrs.footnoteId === footnoteId
                    ) {
                      itemPos = pos;
                      return false;
                    }
                    return true;
                  });
                  if (itemPos === -1) return;
                  const tr = view.state.tr
                    .setSelection(
                      TextSelection.create(view.state.doc, itemPos + 2)
                    )
                    .scrollIntoView();
                  view.dispatch(tr);
                  view.focus();
                }, 0);
                return true;
              }
            }

            // Note number → jump back to the ref
            const backrefEl = target.closest(
              ".footnote-num-backref"
            ) as HTMLElement | null;
            if (backrefEl && view.dom.contains(backrefEl)) {
              const footnoteId = backrefEl.getAttribute(
                "data-target-footnote-id"
              );
              if (footnoteId) {
                event.preventDefault();
                setTimeout(() => {
                  let refPos = -1;
                  let refSize = 1;
                  view.state.doc.descendants((node, pos) => {
                    if (
                      refPos === -1 &&
                      node.type.name === "footnoteRef" &&
                      node.attrs.footnoteId === footnoteId
                    ) {
                      refPos = pos;
                      refSize = node.nodeSize;
                      return false;
                    }
                    return true;
                  });
                  if (refPos === -1) return;
                  const tr = view.state.tr
                    .setSelection(
                      TextSelection.create(view.state.doc, refPos + refSize)
                    )
                    .scrollIntoView();
                  view.dispatch(tr);
                  view.focus();
                }, 0);
                return true;
              }
            }

            // Prevent browser default navigation when clicking links in the editor.
            // This fires at the ProseMirror level, before the browser's default <a> handling.
            const linkElement = target.closest("a");
            if (linkElement && view.dom.contains(linkElement)) {
              event.preventDefault();
            }
            return false; // Let ProseMirror continue processing (cursor placement, etc.)
          },
        },
        handlePaste: (view, event) => {
          const items = event.clipboardData?.items;
          if (!items) return false;
          for (const item of Array.from(items)) {
            if (!item.type.startsWith("image/")) continue;
            const file = item.getAsFile();
            if (!file) continue;
            // Reject images larger than 10MB to prevent browser freeze
            if (file.size > 10 * 1024 * 1024) {
              console.warn(
                "[TipTapEditor] Pasted image too large:",
                (file.size / 1024 / 1024).toFixed(1) + "MB"
              );
              return false;
            }
            // We'll handle the image — prevent default paste
            event.preventDefault();
            const reader = new FileReader();
            reader.onload = async e => {
              const base64 = e.target?.result;
              if (typeof base64 !== "string") return;
              // 1. Instant base64 preview
              const { state } = view;
              const imgNode = state.schema.nodes.image.create({ src: base64 });
              view.dispatch(state.tr.replaceSelectionWith(imgNode));
              // 2. Background upload if callback provided
              const uploadFn = onImageUploadRef.current;
              if (uploadFn) {
                try {
                  const { src: proxyUrl, gcsFileName } = await uploadFn(file);
                  // Build a single transaction to swap src + set gcsFileName
                  const { state: curState } = view;
                  const tr = curState.tr;
                  let found = false;
                  curState.doc.descendants((n, pos) => {
                    if (
                      !found &&
                      n.type.name === "image" &&
                      n.attrs.src === base64
                    ) {
                      tr.setNodeMarkup(pos, undefined, {
                        ...n.attrs,
                        src: proxyUrl,
                        gcsFileName,
                      });
                      found = true;
                    }
                  });
                  if (found) view.dispatch(tr);
                } catch (err) {
                  console.error(
                    "[TipTapEditor] Paste image upload failed:",
                    err
                  );
                  // Revert: remove the orphaned base64 image from the document
                  const { state: errState } = view;
                  const errTr = errState.tr;
                  let removed = false;
                  errState.doc.descendants((n, pos) => {
                    if (
                      !removed &&
                      n.type.name === "image" &&
                      n.attrs.src === base64
                    ) {
                      errTr.delete(pos, pos + n.nodeSize);
                      removed = true;
                    }
                  });
                  if (removed) view.dispatch(errTr);
                }
              }
            };
            reader.readAsDataURL(file);
            return true; // Handled
          }
          return false; // No image found, let default paste proceed
        },
        // Transform pasted HTML before TipTap parses it. Two passes:
        //   1. Normalize inline `font-family` styles to our canonical
        //      preset values (or strip when no canonical match exists).
        //      Without this, pasting from Word with `font-family: Aptos`
        //      lands a `textStyle` mark whose value isn't in the toolbar
        //      dropdown — the dropdown then silently shows the first
        //      option ("Times New Roman") even though the visual font
        //      is something else entirely.
        //   2. Flatten nested tables (>1 deep) to prevent browser freeze.
        transformPastedHTML: (html: string) => {
          // Cheap text-level check before we pay the DOMParser cost.
          const hasFontFamily = html.includes("font-family");
          const tableCount = (html.match(/<table[\s>]/gi) || []).length;
          if (!hasFontFamily && tableCount <= 1) return html;

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          // ─── Pass 1: font-family normalization ───────────────────────
          if (hasFontFamily) {
            const elements = doc.body.querySelectorAll<HTMLElement>(
              '[style*="font-family"]'
            );
            for (const el of Array.from(elements)) {
              const raw = el.style.fontFamily;
              if (!raw) continue;
              const canonical = canonicalizeFontFamily(raw);
              if (canonical) {
                el.style.fontFamily = canonical;
              } else {
                // Unknown font (e.g. Aptos) — strip so the editor's CSS
                // default applies and the dropdown reflects reality.
                el.style.removeProperty("font-family");
                // If the style attribute is now empty, drop it entirely
                // to avoid leaving `<span style="">`.
                if (!el.getAttribute("style")?.trim()) {
                  el.removeAttribute("style");
                }
              }
            }
          }

          // ─── Pass 2: nested-table flatten ────────────────────────────
          if (tableCount > 1) {
            // Check for nesting depth > 1
            let hasDeepNesting = false;
            const checkNesting = (el: Element, depth: number) => {
              if (el.tagName === "TABLE") {
                if (depth > 1) {
                  hasDeepNesting = true;
                  return;
                }
                depth++;
              }
              for (const child of Array.from(el.children)) {
                if (hasDeepNesting) return;
                checkNesting(child, depth);
              }
            };
            checkNesting(doc.body, 0);

            if (hasDeepNesting) {
              // Extract nested tables: move tables nested > 1 level to after their ancestor table
              const flattenTables = (container: Element) => {
                const tables = Array.from(
                  container.querySelectorAll(":scope > * table table")
                );
                for (const nestedTable of tables) {
                  // Find the top-level ancestor table
                  let ancestor: Element = nestedTable;
                  let tableParents = 0;
                  while (
                    ancestor.parentElement &&
                    ancestor.parentElement !== container
                  ) {
                    ancestor = ancestor.parentElement;
                    if (ancestor.tagName === "TABLE") tableParents++;
                  }
                  if (tableParents >= 1) {
                    // Move the nested table after the outermost table
                    const topTable =
                      ancestor.tagName === "TABLE"
                        ? ancestor
                        : ancestor.closest("table");
                    if (topTable && topTable.parentElement) {
                      nestedTable.parentElement?.removeChild(nestedTable);
                      topTable.parentElement.insertBefore(
                        nestedTable,
                        topTable.nextSibling
                      );
                    }
                  }
                }
              };
              flattenTables(doc.body);
            }
          }

          return doc.body.innerHTML;
        },
      },
      content: initialContent || "",
      editable: !readOnly,
      // Guard against plugins (e.g. tiptap-pagination-plus) accessing
      // view.dom when the editor view is unmounted (happens when the
      // browser tab is in the background during long-running SSE updates).
      onCreate: ({ editor: ed }) => {
        const origDispatch = ed.view.dispatch.bind(ed.view);
        ed.view.dispatch = (tr: any) => {
          try {
            origDispatch(tr);
          } catch (e: any) {
            if (!e?.message?.includes("editor view is not available")) {
              throw e;
            }
          }
        };
      },
      onUpdate: ({ editor }) => {
        if (onChange) {
          onChange({
            html: editor.getHTML(),
            json: editor.getJSON(),
            text: editor.getText(),
          });
        }
      },
    });

    // Update editable state when readOnly prop changes
    useEffect(() => {
      if (editor) {
        editor.setEditable(!readOnly);
      }
    }, [editor, readOnly]);

    // Deferred initial content: handles the race condition where the
    // component mounts before initialContent is available (e.g. because
    // document hydration hasn't completed yet).  useEditor only uses the
    // `content` option on first creation — subsequent changes to
    // initialContent are ignored.  This effect catches those late arrivals
    // and applies them to the editor once both the editor instance and
    // meaningful content are ready.
    const hasAppliedDeferredContentRef = useRef(false);
    useEffect(() => {
      if (hasAppliedDeferredContentRef.current) return;
      if (!editor) return;
      // Nothing to set yet
      if (!initialContent) return;
      // Skip empty JSON placeholders like { type: "doc", content: [] }
      if (
        typeof initialContent === "object" &&
        (initialContent as any).type === "doc" &&
        Array.isArray((initialContent as any).content) &&
        (initialContent as any).content.length === 0
      ) {
        return;
      }
      // If editor already has content, mark as done and don't overwrite
      if (editor.getText().trim().length > 0) {
        hasAppliedDeferredContentRef.current = true;
        return;
      }
      // Editor is empty but we now have real content — apply it
      // Flatten deeply nested tables to prevent browser freeze
      let contentToApply = initialContent;
      if (
        typeof contentToApply === "object" &&
        (contentToApply as JSONContent).type === "doc"
      ) {
        const json = contentToApply as JSONContent;
        if (hasDeeplyNestedTables(json)) {
          contentToApply = flattenNestedTables(json);
        }
      }
      editor.commands.setContent(contentToApply);
      hasAppliedDeferredContentRef.current = true;
    }, [editor, initialContent]);

    // Sync currentUser into track changes extension storage
    useEffect(() => {
      if (editor && currentUser) {
        // Set directly on storage for immediate, reliable effect
        const storage = (editor.storage as any)?.trackChanges;
        if (storage) {
          storage.author = currentUser;
        }
      }
    }, [editor, currentUser]);

    // Size table column-resize handles to match their table height
    useEffect(() => {
      if (!editor) return;
      const container = (editor.view.dom as HTMLElement).closest(
        ".tiptap-editor-scroll-wrapper"
      );
      if (!container) return;

      const syncHandleHeights = () => {
        const handles = container.querySelectorAll<HTMLElement>(".handle");
        handles.forEach(handle => {
          // handle → slider div → wrapper div (has the real height;
          // the <table> itself is display:contents so offsetHeight is 0)
          const wrapper = handle.parentElement?.parentElement;
          if (wrapper && wrapper.offsetHeight > 0) {
            handle.style.height = `${wrapper.offsetHeight}px`;
          }
        });
      };

      // Run on DOM changes (tables added/removed, handles created)
      const observer = new MutationObserver(syncHandleHeights);
      observer.observe(container, { childList: true, subtree: true });

      // Also run on resize (table height may change)
      const resizeObserver = new ResizeObserver(syncHandleHeights);
      resizeObserver.observe(container);

      // Initial sync
      syncHandleHeights();

      return () => {
        observer.disconnect();
        resizeObserver.disconnect();
      };
    }, [editor]);

    // ── Header/footer DOM application ──
    // Pushes content into PaginationPlus options AND patches the rendered
    // decoration DOM directly (the extension does not always re-render
    // decorations on option changes alone).
    const applyHeaderFooterArea = useCallback(
      (
        area: "header" | "footer",
        rawLeft: string,
        rawRight: string,
        pageNumber?: number
      ) => {
        if (!editor) return;
        // These strings end up in innerHTML (and PaginationPlus options) and
        // originate from persisted meta — sanitize at the sink regardless of
        // which path (rich compose, legacy flat, live overlay) produced them.
        const left = sanitizeZoneHtml(rawLeft);
        const right = sanitizeZoneHtml(rawRight);
        if (area === "header") {
          if (pageNumber !== undefined) {
            editor.commands.updateHeaderContent(left, right, pageNumber);
          } else {
            editor.commands.updateHeaderContent(left, right);
          }
        } else {
          if (pageNumber !== undefined) {
            editor.commands.updateFooterContent(left, right, pageNumber);
          } else {
            editor.commands.updateFooterContent(left, right);
          }
        }
        const pageNumClass =
          area === "header" ? HEADER_PAGE_NUM_CLASS : FOOTER_PAGE_NUM_CLASS;
        setTimeout(() => {
          const container = editor.view.dom.closest(
            ".tiptap-editor-scroll-wrapper"
          );
          if (!container) return;
          const leftHtml = renderPageTokens(left, pageNumClass);
          const rightHtml = renderPageTokens(right, pageNumClass);
          const patch = (el: Element) => {
            const elLeft = el.querySelector(`.rm-page-${area}-left`);
            const elRight = el.querySelector(`.rm-page-${area}-right`);
            if (elLeft) elLeft.innerHTML = leftHtml;
            if (elRight) elRight.innerHTML = rightHtml;
          };
          if (pageNumber !== undefined) {
            const el = container.querySelector(
              `.rm-page-${area}-${pageNumber}`
            );
            if (el) patch(el);
          } else {
            container.querySelectorAll(`.rm-page-${area}`).forEach(patch);
          }
        }, 50);
      },
      [editor]
    );

    // Apply a full rich config to the page decorations (default + page 1)
    const applyRichHeaderFooter = useCallback(
      (cfg: ParsedHeaderFooterConfig) => {
        if (!editor) return;
        const headerHtml = composeSectionHtml(
          cfg.defaultHeader,
          HEADER_PAGE_NUM_CLASS
        );
        const footerHtml = composeSectionHtml(
          cfg.defaultFooter,
          FOOTER_PAGE_NUM_CLASS
        );
        applyHeaderFooterArea("header", headerHtml, "");
        applyHeaderFooterArea("footer", footerHtml, "");
        // Page-1 override — falls back to the default content when
        // "different first page" is off (clears any stale override)
        const fpHeader =
          cfg.hasTitlePage && cfg.firstPageHeader
            ? composeSectionHtml(cfg.firstPageHeader, HEADER_PAGE_NUM_CLASS)
            : headerHtml;
        const fpFooter =
          cfg.hasTitlePage && cfg.firstPageFooter
            ? composeSectionHtml(cfg.firstPageFooter, FOOTER_PAGE_NUM_CLASS)
            : footerHtml;
        applyHeaderFooterArea("header", fpHeader, "", 1);
        applyHeaderFooterArea("footer", fpFooter, "", 1);
      },
      [editor, applyHeaderFooterArea]
    );

    // Sync rich config from the parent (e.g. after DOCX import or doc load)
    useEffect(() => {
      if (!richMode || headerFooterRich === undefined || !editor) return;
      const normalized = normalizeParsedConfig(headerFooterRich);
      const json = JSON.stringify(normalized);
      if (json === richConfigJsonRef.current) return;
      richConfigJsonRef.current = json;
      setRichConfig(normalized);
      applyRichHeaderFooter(normalized);
    }, [richMode, headerFooterRich, editor, applyRichHeaderFooter]);

    // Live updates while the in-place editor is open
    const handleHfLiveChange = useCallback(
      (cfg: ParsedHeaderFooterConfig) => {
        richConfigJsonRef.current = JSON.stringify(cfg);
        setRichConfig(cfg);
        applyRichHeaderFooter(cfg);
      },
      [applyRichHeaderFooter]
    );

    // Editing finished — persist via the parent
    const handleHfClose = useCallback(
      (cfg: ParsedHeaderFooterConfig) => {
        handleHfLiveChange(cfg);
        setHfEditing(null);
        if (onHeaderFooterRichChange) onHeaderFooterRichChange(cfg);
      },
      [handleHfLiveChange, onHeaderFooterRichChange]
    );

    // Menu → Insert → Header & Footer: open the in-place editor on the
    // first page header (rich mode) or defer to the legacy callback.
    const handleEditHeaderFooterRequest = useCallback(() => {
      if (!richMode) {
        if (onEditHeaderFooter) onEditHeaderFooter();
        return;
      }
      if (readOnlyRef.current || !editor) return;
      const container = editor.view.dom.closest(
        ".tiptap-editor-scroll-wrapper"
      );
      // Target the VISIBLE first-page header (.rm-first-page-header). A bare
      // ".rm-page-header" returns the first match in DOM order — the page-2
      // header inside the #pages wrapper — which is display:none (0×0) on a
      // single-page document, so the overlay opened as an invisible sliver and
      // the menu item "did nothing". Fall back to any header only if the
      // first-page one isn't present.
      const headerEl = (container?.querySelector(".rm-first-page-header") ||
        container?.querySelector(".rm-page-header")) as HTMLElement | null;
      if (headerEl) {
        headerEl.scrollIntoView({ block: "center", behavior: "auto" });
        setHfEditing({ kind: "header", pageNumber: 1, targetEl: headerEl });
      }
    }, [richMode, onEditHeaderFooter, editor]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getHTML: () => editor?.getHTML() || "",
      getJSON: () => editor?.getJSON() || { type: "doc", content: [] },
      getText: () => editor?.getText() || "",
      setContent: (content: string | JSONContent) => {
        if (editor) {
          // Flatten deeply nested tables to prevent browser freeze
          let safeContent = content;
          if (
            typeof content === "object" &&
            (content as JSONContent).type === "doc"
          ) {
            const json = content as JSONContent;
            if (hasDeeplyNestedTables(json)) {
              safeContent = flattenNestedTables(json);
            }
          }

          // Suppress mark-stripping in TrackChangesExtension while we
          // programmatically replace the document.  Other extensions
          // (e.g. pagination) may react to setContent with follow-up
          // transactions that lack addToHistory:false, which would
          // otherwise cause the appendTransaction guard to treat them
          // as user input and strip track-change marks.
          const tcStorage = (editor.storage as any).trackChanges;
          if (tcStorage) tcStorage.suppressMarkStripping = true;

          editor.commands.setContent(safeContent);

          // Clear the flag after a microtask so that all synchronous
          // appendTransaction rounds triggered by setContent are covered,
          // but subsequent genuine user edits are processed normally.
          setTimeout(() => {
            if (tcStorage) tcStorage.suppressMarkStripping = false;
          }, 0);
        }
      },
      focus: () => editor?.commands.focus(),
      isEmpty: () => editor?.isEmpty || true,
      getEditor: () => editor,
      insertContent: (content: string | JSONContent) => {
        editor?.commands.insertContent(content);
      },
      insertImage: (src: string, alt?: string) => {
        editor?.commands.setImage({ src, alt });
      },
      insertTable: (rows = 3, cols = 3) => {
        editor
          ?.chain()
          .focus()
          .insertTable({ rows, cols, withHeaderRow: true })
          .run();
      },
      addComment: (commentId: string) => {
        editor?.chain().focus().setComment(commentId).run();
      },
      removeComment: (commentId: string) => {
        editor?.chain().focus().unsetComment(commentId).run();
      },
      scrollToComment: (commentId: string) => {
        if (!editor) return false;

        const { doc } = editor.state;
        let found = false;
        let commentFrom = -1;
        let _commentTo = -1;

        // Find the position of the comment mark in the document
        doc.descendants((node, pos) => {
          if (found) return false; // Stop searching once found

          if (node.marks) {
            for (const mark of node.marks) {
              if (
                mark.type.name === "comment" &&
                mark.attrs.commentId === commentId
              ) {
                commentFrom = pos;
                _commentTo = pos + node.nodeSize;
                found = true;
                return false; // Stop searching
              }
            }
          }
        });

        if (found && commentFrom >= 0) {
          // Find the DOM element with this comment and add highlight class
          const editorElement = editor.view.dom;
          const commentElement = editorElement.querySelector(
            `[data-comment-id="${commentId}"]`
          ) as HTMLElement;

          if (commentElement) {
            // Add a prominent highlight class
            commentElement.classList.add("comment-clicked-highlight");

            // Remove the highlight after a delay
            setTimeout(() => {
              commentElement.classList.remove("comment-clicked-highlight");
            }, 2000);

            // Scroll to center the comment in the editor
            const scrollContainer = editorElement.closest(
              ".tiptap-editor-content"
            ) as HTMLElement;
            if (scrollContainer) {
              const containerHeight = scrollContainer.clientHeight;
              const commentRect = commentElement.getBoundingClientRect();
              const containerRect = scrollContainer.getBoundingClientRect();

              // Calculate the comment's position relative to the scrollable content
              const commentTopInContent =
                commentRect.top - containerRect.top + scrollContainer.scrollTop;
              const commentMiddleInContent =
                commentTopInContent + commentRect.height / 2;

              // Calculate scroll position to center the comment in the viewport
              const targetScrollTop =
                commentMiddleInContent - containerHeight / 2;

              // Use instant scroll first, then the visual effect is clear
              scrollContainer.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: "smooth",
              });
            }
          }

          return true;
        }

        return false;
      },
      scrollToTrackChange: (changeId: string) => {
        if (!editor) return false;

        // Find the DOM element with this change ID (either insertion or deletion)
        const editorElement = editor.view.dom;
        const changeElement = editorElement.querySelector(
          `[data-change-id="${changeId}"]`
        ) as HTMLElement;

        if (changeElement) {
          // Add a prominent highlight class
          changeElement.classList.add("track-change-clicked-highlight");

          // Remove the highlight after a delay
          setTimeout(() => {
            changeElement.classList.remove("track-change-clicked-highlight");
          }, 2000);

          // Scroll to center the change in the editor
          const scrollContainer = editorElement.closest(
            ".tiptap-editor-content"
          ) as HTMLElement;
          if (scrollContainer) {
            const containerHeight = scrollContainer.clientHeight;
            const changeRect = changeElement.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();

            // Calculate the change's position relative to the scrollable content
            const changeTopInContent =
              changeRect.top - containerRect.top + scrollContainer.scrollTop;
            const changeMiddleInContent =
              changeTopInContent + changeRect.height / 2;

            // Calculate scroll position to center the change in the viewport
            const targetScrollTop = changeMiddleInContent - containerHeight / 2;

            scrollContainer.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: "smooth",
            });
          }

          return true;
        }

        return false;
      },
      // Track changes methods (using track-change-extension package)
      getTrackChanges: () => {
        if (!editor) return [];
        return getTrackChanges(editor);
      },
      acceptChange: (changeId: string) => {
        editor?.commands.acceptChange(changeId);
      },
      rejectChange: (changeId: string) => {
        editor?.commands.rejectChange(changeId);
      },
      acceptAllChanges: () => {
        editor?.commands.acceptAllChanges();
      },
      rejectAllChanges: () => {
        editor?.commands.rejectAllChanges();
      },
      enableTrackChanges: () => {
        editor?.commands.enableTrackChanges();
      },
      disableTrackChanges: () => {
        editor?.commands.disableTrackChanges();
      },
      toggleTrackChanges: () => {
        editor?.commands.toggleTrackChanges();
      },
      isTrackChangesEnabled: () => {
        // Access the storage through the editor
        return (editor?.storage as any)?.trackChanges?.enabled || false;
      },
      setTrackChangesAuthor: (author: string) => {
        // Set directly on storage for immediate effect (bypasses command dispatch)
        const storage = (editor?.storage as any)?.trackChanges;
        if (storage) {
          storage.author = author;
        }
      },
      getTrackChangesAuthor: () => {
        return (editor?.storage as any)?.trackChanges?.author as
          | string
          | undefined;
      },
      // Apply track change mark to text (for import)
      applyTrackChangeMark: (
        text: string,
        type: "insertion" | "deletion",
        changeId: string,
        author?: string,
        date?: string,
        position?: number,
        contextBefore?: string,
        contextAfter?: string
      ) => {
        if (!editor || !text) return false;

        const { doc } = editor.state;
        const docText = doc.textContent;
        const searchText = text.trim();
        let found = false;

        // Build mark attributes
        const markType = type === "insertion" ? "insertion" : "deletion";
        const markAttrs: Record<string, any> = { changeId };
        if (author) markAttrs.author = author;
        if (date) markAttrs.date = date;

        // Context strings for verification (50 chars)
        const ctxBefore50 = (contextBefore || "").slice(-50);
        const ctxAfter50 = (contextAfter || "").slice(0, 50);

        // Helper: Calculate similarity percentage between two strings
        const calculateSimilarity = (str1: string, str2: string): number => {
          if (!str1 || !str2) return 0;
          const len = Math.min(str1.length, str2.length);
          if (len === 0) return 0;
          let matches = 0;
          for (let i = 0; i < len; i++) {
            if (str1[i] === str2[i]) matches++;
          }
          return matches / len;
        };

        // Helper: Apply mark at a text position in the document
        const applyMarkAtTextPos = (textPosition: number): boolean => {
          let textPos = 0;
          let applied = false;

          doc.descendants((node, pos) => {
            if (applied) return false;

            if (node.isText && node.text) {
              const nodeEnd = textPos + node.text.length;

              if (textPosition >= textPos && textPosition < nodeEnd) {
                const offset = textPosition - textPos;
                const from = pos + offset;
                const to = from + searchText.length;

                editor
                  .chain()
                  .setTextSelection({ from, to })
                  .setMark(markType, markAttrs)
                  .run();

                applied = true;
                return false;
              }
              textPos = nodeEnd;
            }
          });

          return applied;
        };

        // Helper: Verify context at a position (returns true if context matches well)
        const verifyContext = (textPosition: number): boolean => {
          const beforeInDoc = docText.substring(
            Math.max(0, textPosition - 50),
            textPosition
          );
          const afterInDoc = docText.substring(
            textPosition + searchText.length,
            textPosition + searchText.length + 50
          );

          const beforeSimilarity = calculateSimilarity(
            beforeInDoc.slice(-ctxBefore50.length),
            ctxBefore50.slice(-beforeInDoc.length)
          );
          const afterSimilarity = calculateSimilarity(
            afterInDoc.slice(0, ctxAfter50.length),
            ctxAfter50.slice(0, afterInDoc.length)
          );

          // Need at least 95% match on available context (strict verification)
          // High threshold ensures we only accept position if context truly matches
          const hasContextBefore = ctxBefore50.length > 0;
          const hasContextAfter = ctxAfter50.length > 0;

          if (hasContextBefore && hasContextAfter) {
            return (beforeSimilarity + afterSimilarity) / 2 >= 0.95;
          } else if (hasContextBefore) {
            return beforeSimilarity >= 0.95;
          } else if (hasContextAfter) {
            return afterSimilarity >= 0.95;
          }
          return true; // No context to verify
        };

        // Helper: Find best match using context scoring
        const findBestContextMatch = (): number => {
          let bestPos = -1;
          let bestScore = 0;

          let searchPos = 0;
          while (searchPos < docText.length) {
            const idx = docText.indexOf(searchText, searchPos);
            if (idx === -1) break;

            // Calculate context match score
            const beforeInDoc = docText.substring(Math.max(0, idx - 50), idx);
            const afterInDoc = docText.substring(
              idx + searchText.length,
              idx + searchText.length + 50
            );

            let score = 0;

            // Score based on character matches in context
            if (ctxBefore50) {
              const compareLen = Math.min(
                beforeInDoc.length,
                ctxBefore50.length
              );
              for (let i = 1; i <= compareLen; i++) {
                if (beforeInDoc.slice(-i) === ctxBefore50.slice(-i)) {
                  score += i;
                }
              }
            }

            if (ctxAfter50) {
              const compareLen = Math.min(afterInDoc.length, ctxAfter50.length);
              for (let i = 1; i <= compareLen; i++) {
                if (afterInDoc.slice(0, i) === ctxAfter50.slice(0, i)) {
                  score += i;
                }
              }
            }

            if (score > bestScore) {
              bestScore = score;
              bestPos = idx;
            }

            searchPos = idx + 1;
          }

          // Require minimum score threshold
          return bestScore >= 10 ? bestPos : -1;
        };

        // ============================================================
        // STEP 1: Position-based lookup
        // ============================================================
        if (position !== undefined && position >= 0) {
          // Check if text exists at the expected position
          const textAtPos = docText.substring(
            position,
            position + searchText.length
          );

          if (textAtPos === searchText) {
            // ============================================================
            // STEP 2: Verify with context
            // ============================================================
            if (verifyContext(position)) {
              // Context verified! Apply the mark
              if (applyMarkAtTextPos(position)) {
                return true;
              }
            } else {
            }
          } else {
          }
        }

        // ============================================================
        // STEP 3: Context matching fallback (50 chars before/after)
        // ============================================================
        if (!found && (ctxBefore50 || ctxAfter50)) {
          const bestMatchPos = findBestContextMatch();

          if (bestMatchPos >= 0) {
            if (applyMarkAtTextPos(bestMatchPos)) {
              return true;
            }
          }
        }

        // ============================================================
        // STEP 4: First match fallback (ONLY for long, unique text)
        // ============================================================
        // Only use indexOf fallback for text that is long enough to be
        // unique in the document. Short text (like "n", "-", "or") would
        // match the wrong occurrence, creating phantom track changes.
        if (!found && searchText.length >= 30) {
          const firstMatchPos = docText.indexOf(searchText);

          if (firstMatchPos >= 0) {
            if (applyMarkAtTextPos(firstMatchPos)) {
              return true;
            }
          }
        }

        return false;
      },
      // Footnotes / endnotes
      insertFootnote: (kind: FootnoteKind = "footnote") => {
        editor?.chain().focus().insertFootnote(kind).run();
      },
      getFootnotes: () => {
        if (!editor) return [];
        return getFootnotes(editor.state.doc);
      },
      // Math/LaTeX methods
      insertMath: (latex: string, isBlock = false) => {
        if (editor) {
          if (isBlock) {
            // Insert block math (displayed on its own line)
            editor
              .chain()
              .focus()
              .insertContent({
                type: "blockMath",
                attrs: { latex },
              })
              .run();
          } else {
            // Insert inline math
            editor
              .chain()
              .focus()
              .insertContent({
                type: "inlineMath",
                attrs: { latex },
              })
              .run();
          }
        }
      },
      // === Document Operations API (AI Integration) ===
      getDocumentForLLM: () => {
        if (!editor) return [];
        return serializeForLLM(editor);
      },
      applyOperations: (patch: OperationPatch) => {
        if (!editor) {
          return {
            success: false,
            results: [],
            failedCount: patch.operations.length,
            comments: [],
          };
        }
        return executeOperations(editor, patch);
      },
      validateOperations: (patch: OperationPatch) => {
        if (!editor) {
          return { valid: false, errors: ["Editor not available"] };
        }
        return validatePatch(editor, patch);
      },
      findNodeById: (nodeId: string) => {
        if (!editor) return null;
        return findNodeById(editor.state.doc, nodeId);
      },
      lockForAI: () => {
        setIsAILocked(true);
        editor?.setEditable(false);
      },
      unlockFromAI: () => {
        setIsAILocked(false);
        editor?.setEditable(!readOnly);
      },
      isLockedForAI: () => isAILocked,
      // === Comment Sync (for undo/redo support) ===
      getCommentIds: () => {
        if (!editor) return [];
        const commentIds = new Set<string>();
        const { doc } = editor.state;

        // Scan the entire document for comment marks
        doc.descendants(node => {
          if (node.marks) {
            node.marks.forEach(mark => {
              if (mark.type.name === "comment" && mark.attrs.commentId) {
                commentIds.add(mark.attrs.commentId);
              }
            });
          }
        });

        return Array.from(commentIds);
      },
      // === Header/Footer API ===
      updateHeaderContent: (
        left: string,
        right: string,
        pageNumber?: number
      ) => {
        applyHeaderFooterArea("header", left, right, pageNumber);
      },
      updateFooterContent: (
        left: string,
        right: string,
        pageNumber?: number
      ) => {
        applyHeaderFooterArea("footer", left, right, pageNumber);
      },
      getHeaderFooterConfig: () => {
        return {
          headerLeft,
          headerRight,
          footerLeft,
          footerRight,
        };
      },
      zoomToFit: () => {
        const container = document.querySelector(".tiptap-editor-content");
        if (container) {
          const containerWidth = container.clientWidth;
          const pageWidth = pageDims.pageWidth;
          const padding = 40;
          const fitLevel = Math.round(
            ((containerWidth - padding) / pageWidth) * 100
          );
          setZoomLevel(Math.min(100, Math.max(50, fitLevel)));
        }
      },
    }));

    // Ref for tracking mouse hover over the editor container (must be before any early return)
    const containerRef = useRef<HTMLDivElement>(null);
    const isMouseInsideRef = useRef(false);

    // Track mouse enter/leave on the editor container
    const handleMouseEnter = useCallback(() => {
      isMouseInsideRef.current = true;
    }, []);
    const handleMouseLeave = useCallback(() => {
      isMouseInsideRef.current = false;
    }, []);

    // Global keydown listener to intercept Ctrl/Cmd shortcuts when mouse is over editor
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!isMouseInsideRef.current) return;
        const isMod = e.metaKey || e.ctrlKey;
        if (!isMod) return;

        // While the agent is streaming edits (AI lock), block shortcuts that
        // could dispatch a transaction into the document mid-patch — Find &
        // Replace (Ctrl+H), link, save, new. Zoom is view-only and stays live.
        if (isAILocked && !["=", "+", "-", "0"].includes(e.key)) return;

        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          setZoomLevel(prev => Math.min(200, prev + 10));
        } else if (e.key === "-") {
          e.preventDefault();
          setZoomLevel(prev => Math.max(50, prev - 10));
        } else if (e.key === "0") {
          e.preventDefault();
          setZoomLevel(100);
        } else if (e.key === "f" || e.key === "F") {
          e.preventDefault();
          setShowFindReplace(true);
          setFindReplaceMode("find");
        } else if (e.key === "h" || e.key === "H") {
          e.preventDefault();
          setShowFindReplace(true);
          setFindReplaceMode("replace");
        } else if (e.key === "k" || e.key === "K") {
          if (!readOnly) {
            e.preventDefault();
            setShowLinkModal(true);
          }
        } else if (e.key === "s" || e.key === "S") {
          if (onSave) {
            e.preventDefault();
            onSave();
          }
        } else if (e.key === "p" || e.key === "P") {
          if (onPrint) {
            e.preventDefault();
            onPrint();
          }
        } else if (e.key === "n" || e.key === "N") {
          if (onNewDocument) {
            e.preventDefault();
            onNewDocument();
          }
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [readOnly, onSave, onPrint, onNewDocument, isAILocked]);

    // Apply the document watermark as a repeating, low-opacity overlay on the
    // editor content. Driven by a CSS var on the ProseMirror element so the CSS
    // rule can tile it; a data attribute toggles the ::before layer on/off.
    useEffect(() => {
      if (!editor) return;
      const dom = editor.view.dom as HTMLElement;
      const text = (watermark || "").trim();
      if (!text) {
        dom.style.removeProperty("--tiptap-wm-image");
        dom.removeAttribute("data-watermark");
        return;
      }
      const esc = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const svg =
        `<svg xmlns='http://www.w3.org/2000/svg' width='480' height='320'>` +
        `<text x='240' y='170' fill='rgba(120,120,120,0.16)' ` +
        `font-family='Arial, sans-serif' font-size='46' font-weight='700' ` +
        `text-anchor='middle' transform='rotate(-32 240 160)'>${esc}</text></svg>`;
      dom.style.setProperty(
        "--tiptap-wm-image",
        `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
      );
      dom.setAttribute("data-watermark", "true");
    }, [editor, watermark]);

    if (!editor) {
      return null;
    }

    return (
      <div
        ref={containerRef}
        className={`tiptap-editor-container ${className} ${isAILocked ? "ai-locked" : ""}`}
        style={{ "--editor-zoom": zoomLevel / 100 } as React.CSSProperties}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {showToolbar && !readOnly && (
          <>
            {/* Top Menu Bar (File, Edit, View, etc.) */}
            <EditorMenuBar
              editor={editor}
              onNewDocument={onNewDocument}
              onSave={onSave}
              isSaving={isSaving}
              onImportDocx={onImportDocx}
              onImportPdf={onImportPdf}
              onExportDocx={onExportDocx}
              onExportPdf={onExportPdf}
              onPrint={onPrint}
              // View menu - Track Changes
              trackChangesEnabled={trackChangesEnabled}
              onToggleTrackChanges={onToggleTrackChanges}
              showTrackChangesPanel={showTrackChangesPanel}
              onToggleTrackChangesPanel={onToggleTrackChangesPanel}
              trackChangesCount={trackChangesCount}
              // View menu - Comments
              showCommentsPanel={showCommentsPanel}
              onToggleCommentsPanel={onToggleCommentsPanel}
              commentsCount={commentsCount}
              // View menu - Outline
              showOutlinePanel={showOutlinePanel}
              onToggleOutlinePanel={onToggleOutlinePanel}
              // Edit menu - Find & Replace
              onFindReplace={() => {
                setShowFindReplace(true);
                setFindReplaceMode("find");
              }}
              // Insert menu
              onAddComment={onAddComment}
              onInsertMath={() => setShowMathModal(true)}
              onInsertSpecialChar={() => setShowSpecialCharModal(true)}
              onWatermark={
                onWatermarkChange
                  ? () => setShowWatermarkModal(true)
                  : undefined
              }
              onInsertLink={() => setShowLinkModal(true)}
              onEditHeaderFooter={
                richMode || onEditHeaderFooter
                  ? handleEditHeaderFooterRequest
                  : undefined
              }
              onPageSetup={onPageSetup}
              // Tools menu
              onShowTextStats={onShowTextStats}
              // Version history
              onVersionHistory={onVersionHistory}
              // Help menu
              onShowKeyboardShortcuts={() => setShowShortcutsModal(true)}
              readOnly={readOnly}
              onImageUpload={onImageUpload}
            />
            {/* Formatting Toolbar */}
            <EditorToolbar
              editor={editor}
              onExportDocx={onExportDocx}
              onImportDocx={onImportDocx}
              // Save
              onSave={onSave}
              isSaving={isSaving}
              showUnsavedBadge={showUnsavedBadge}
              // Track changes
              trackChangesEnabled={trackChangesEnabled}
              onToggleTrackChanges={onToggleTrackChanges}
              trackChanges={trackChanges}
              currentChangeIndex={currentChangeIndex}
              onNavigateChange={onNavigateChange}
              onAcceptChange={onAcceptChange}
              onRejectChange={onRejectChange}
              onAcceptAllChanges={onAcceptAllChanges}
              onRejectAllChanges={onRejectAllChanges}
              // Comments panel
              showCommentsPanel={showCommentsPanel}
              onToggleCommentsPanel={onToggleCommentsPanel}
              commentsCount={commentsCount}
              onAddComment={onAddComment}
              // Track changes panel
              showTrackChangesPanel={showTrackChangesPanel}
              onToggleTrackChangesPanel={onToggleTrackChangesPanel}
              trackChangesCount={trackChangesCount}
              // Text statistics
              onShowTextStats={onShowTextStats}
              // Read-only mode
              readOnly={readOnly}
              // Zoom
              zoomLevel={zoomLevel}
              onZoomChange={setZoomLevel}
              // Find & Replace
              onFindReplace={() => {
                setShowFindReplace(true);
                setFindReplaceMode("find");
              }}
              // Link
              onInsertLink={() => setShowLinkModal(true)}
              onImageUpload={onImageUpload}
            />
          </>
        )}
        {/* Find & Replace Bar */}
        {showFindReplace && editor && (
          <FindReplaceBar
            editor={editor}
            onClose={() => setShowFindReplace(false)}
            showReplace={findReplaceMode === "replace"}
          />
        )}
        <div
          className={`tiptap-editor-scroll-wrapper${(floatingShowComments && (floatingComments?.length || floatingPendingCommentId)) || (floatingShowTrackChanges && floatingTrackChanges?.length) ? " has-floating-annotations" : ""}`}
          onClick={e => {
            // Dismiss active floating cards when clicking outside them
            if (
              floatingOnDismiss &&
              !(e.target as HTMLElement).closest(".floating-card")
            ) {
              floatingOnDismiss();
            }
          }}
        >
          <TipTapEditorContent
            editor={editor}
            className="tiptap-editor-content"
            onContextMenu={(e: React.MouseEvent) => {
              // Show table context menu when right-clicking inside a table cell
              const target = e.target as HTMLElement;
              const cell = target.closest("td, th");
              if (cell && !readOnly) {
                e.preventDefault();
                setTableMenuPosition({ x: e.clientX, y: e.clientY });
              }
            }}
          />
          {/* In-place header/footer editor (rich mode) */}
          {richMode && hfEditing && !readOnly && (
            <HeaderFooterInlineEditor
              kind={hfEditing.kind}
              pageNumber={hfEditing.pageNumber}
              targetEl={hfEditing.targetEl}
              config={richConfig}
              onLiveChange={handleHfLiveChange}
              onClose={handleHfClose}
              onImageUpload={onImageUpload}
            />
          )}
          {/* Floating annotation cards (Google Docs-style margin cards) */}
          {editor && (floatingShowComments || floatingShowTrackChanges) && (
            <FloatingAnnotationsLayer
              editor={editor}
              comments={floatingComments || []}
              activeCommentId={floatingActiveCommentId || null}
              showComments={!!floatingShowComments}
              currentUser={currentUser}
              aiEditingCommentId={floatingAiEditingCommentId}
              onCommentClick={floatingOnCommentClick || (() => {})}
              onDeleteComment={floatingOnDeleteComment || (() => {})}
              onResolveComment={floatingOnResolveComment}
              onEditComment={floatingOnEditComment}
              onReplyToComment={floatingOnReplyToComment || (() => {})}
              onAIEditFromComment={floatingOnAIEditFromComment}
              trackChanges={floatingTrackChanges || []}
              activeChangeId={floatingActiveChangeId || null}
              showTrackChanges={!!floatingShowTrackChanges}
              onChangeClick={floatingOnChangeClick || (() => {})}
              onAcceptChange={floatingOnAcceptChange || (() => {})}
              onRejectChange={floatingOnRejectChange || (() => {})}
              readOnly={readOnly}
              pendingCommentId={floatingPendingCommentId}
              onSubmitPendingComment={floatingOnSubmitPendingComment}
              onCancelPendingComment={floatingOnCancelPendingComment}
            />
          )}
        </div>

        {/* Table Context Menu (right-click on table cells) */}
        {editor && tableMenuPosition && (
          <TableContextMenu
            editor={editor}
            position={tableMenuPosition}
            onClose={() => setTableMenuPosition(null)}
          />
        )}

        {/* Math Input Modal (for Insert menu) */}
        <MathInputModal
          show={showMathModal}
          onHide={() => setShowMathModal(false)}
          onInsert={(latex, isBlock) => {
            editor
              ?.chain()
              .focus()
              .insertContent({
                type: isBlock ? "blockMath" : "inlineMath",
                attrs: { latex },
              })
              .run();
          }}
        />

        <SpecialCharacterModal
          show={showSpecialCharModal}
          onHide={() => setShowSpecialCharModal(false)}
          onInsert={char => {
            editor?.chain().focus().insertContent(char).run();
          }}
        />

        <WatermarkModal
          show={showWatermarkModal}
          onHide={() => setShowWatermarkModal(false)}
          value={watermark}
          onApply={text => onWatermarkChange?.(text)}
        />

        {/* Link Bubble (Google Docs-style popover on link click) */}
        {editor && (
          <LinkBubble editor={editor} onEdit={() => setShowLinkModal(true)} />
        )}

        {/* Link Modal */}
        {editor && (
          <LinkModal
            show={showLinkModal}
            onHide={() => setShowLinkModal(false)}
            editor={editor}
          />
        )}

        {/* Keyboard Shortcuts Modal */}
        <KeyboardShortcutsModal
          show={showShortcutsModal}
          onHide={() => setShowShortcutsModal(false)}
        />
      </div>
    );
  }
);

TipTapEditor.displayName = "TipTapEditor";

// Error boundary: catches extension crashes and renders a recovery UI
// instead of unmounting the entire page.
class TipTapEditorErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(
      "[TipTapEditor] Unhandled error:",
      error,
      info.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
          <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            The editor encountered an error.
          </p>
          <p style={{ fontSize: 13, marginBottom: 16 }}>
            {this.state.error?.message || "Unknown error"}
          </p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              padding: "6px 16px",
              fontSize: 13,
              borderRadius: 6,
              border: "1px solid #ccc",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const TipTapEditorWithBoundary = forwardRef<TipTapEditorRef, TipTapEditorProps>(
  (props, ref) => (
    <TipTapEditorErrorBoundary>
      <TipTapEditor ref={ref} {...props} />
    </TipTapEditorErrorBoundary>
  )
);
TipTapEditorWithBoundary.displayName = "TipTapEditorWithBoundary";

export default TipTapEditorWithBoundary;
